// THE BRIDGE — deterministic execution governor.
//
// Reads recorded Bridge45 state and emits a briefing + recommendations.
// It does not invent tasks, does not mutate strategy, and never auto-applies
// Primary D001 or Strategy Exception changes. Why? text is templated from facts.

import { evidenceWeight, isOpenAction, newAction, recommendReadyMoves } from './actions';
import { candidateScore } from './funnel';
import { deriveGateReadiness } from './gates';
import { isoWeekday, isAfter, addDays } from './dates';
import { weightOf, fmtMonths, fmtMoney } from './derive';
import { stopContinueConcentrate } from './coach';
import { PRIORITY_CONFIG_V1, scoringConfigVersion } from './scoring';
import { deriveOperatingMode } from './operatingMode';

const candidateTotal = (opp) => candidateScore(opp).total;

function recId(kind, key) {
  return `rec-${kind}-${key}`.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 80);
}

function suppressed(overrides, kind, subject) {
  return (overrides || []).some(
    (o) =>
      (o.status === 'overridden' || o.status === 'rejected') &&
      o.kind === kind &&
      (o.payload?.subject === subject || o.payload?.actionId === subject || o.payload?.candidateId === subject),
  );
}

function whyAction(a, derived) {
  const bits = [];
  if (a.whyNow) return a.whyNow;
  if (a.dueDate && derived.sprint?.executionAsOf && isAfter(derived.sprint.executionAsOf, a.dueDate)) {
    bits.push(`it is overdue (${a.dueDate})`);
  }
  if (derived.sprint?.nextGate && String(a.gateRelevance) === String(derived.sprint.nextGate.day)) {
    bits.push(`it is marked for ${derived.sprint.nextGate.label}`);
  }
  if (a.linkedLabel) bits.push(`it is linked to ${a.linkedLabel}`);
  if (evidenceWeight(a.expectedEvidence) >= 4) bits.push(`target evidence is ${a.expectedEvidence}`);
  if (a.expectedEvidence === 'OPS') bits.push('it is an internal enabler with no evidence credit');
  if (a.priority === 'Critical' || a.priority === 'High') bits.push(`priority is ${a.priority}`);
  const because = bits.length ? bits.join(', ') : 'it is the highest-scoring eligible move against the locked strategy';
  return `Because ${because}.`;
}

function delayConsequence(a, derived) {
  if (derived.sprint?.nextGate && String(a.gateRelevance) === String(derived.sprint.nextGate.day)) {
    return `Delay means ${derived.sprint.nextGate.label} is judged without this evidence.`;
  }
  if (a.linkedKind === 'opportunity' && a.linkedLabel) {
    return `Delay lets ${a.linkedLabel} go stale while the queue grows.`;
  }
  if (a.dueDate) return `Delay leaves ${a.text} past ${a.dueDate}.`;
  return 'Impact cannot yet be determined.';
}

function evaluateMorning(state, derived) {
  const { sprint, runway, d001, saudi, infra, time, funnel, top3, evidence } = derived;
  const gate = deriveGateReadiness(state, derived, sprint.nextGate?.day);
  const leak = funnel.diagnosis[0];
  const scored = (funnel.candidates || [])
    .map((o) => ({ o, s: candidateTotal(o) }))
    .sort((a, b) => b.s - a.s);
  const best = scored[0];
  const primaryScore = funnel.primary ? candidateTotal(funnel.primary) : 0;
  const betterCandidate = best && funnel.primary && best.o.id !== funnel.primary.id && best.s - primaryScore >= 5;

  const e3e4 = (state.opportunities || []).filter(
    (o) => o.stage !== 'Won / Lost / Parked' && evidenceWeight(o.evidenceLevel) >= 3,
  );

  return {
    constraint: pickConstraint(state, derived, leak),
    missingGateEvidence: gate.missing.map((m) => m.label),
    e3e4Advance: e3e4.slice(0, 3).map((o) => o.name || o.accountName || o.id),
    overdue: (top3.overdue || []).map((a) => a.text),
    primaryStillBest: funnel.primary ? !betterCandidate : null,
    betterCandidate: betterCandidate ? { id: best.o.id, name: best.o.name, score: best.s, primaryScore } : null,
    funnelHealth: funnel.health?.status || 'unknown',
    funnelWhy: funnel.health?.why || '',
    saudiEarned: saudi.count >= 2,
    saudiCount: saudi.count,
    closestInfra: infra.closest
      ? { account: infra.closest.account, stage: infra.closest.stage, nextAction: infra.closest.nextAction }
      : null,
    timeDrifting: !!time.drifting || (time.internalShare !== null && time.internalShare > 30),
    timeInternalShare: time.internalShare,
    runwayConstraint: runway.survivesSprint === false || runway.status === 'critical',
    runwayStatus: runway.status,
    runwayMonths: runway.months,
    weekProof: (evidence.weekProof || []).length,
    weekActivity: (evidence.thisWeek || []).filter((e) => e.level === 'ACT').length,
  };
}

function pickConstraint(state, derived, leak) {
  const { runway, d001, sprint, funnel, time } = derived;
  const accounts = (state.accounts || []).length;
  const commercialUninitialized = accounts === 0;
  const contacted = funnel.counts?.contacted || 0;
  const readyPool = Math.max(funnel.counts?.tierA || 0, funnel.counts?.qualifiedTargets || 0);

  if (runway.survivesSprint === false && !runway.cashUnknown && !runway.burnUnknown) {
    return {
      id: 'constraint-runway-sprint',
      title: 'Runway does not cover the sprint',
      diagnosis: `Current cash and burn do not reach ${sprint.end}. This is the constraint, whatever else is on the board.`,
      source: 'runway',
      factIds: ['runway.survivesSprint'],
    };
  }
  if (runway.status === 'critical' && !runway.cashUnknown && !runway.burnUnknown) {
    return {
      id: 'constraint-runway-critical',
      title: 'Runway is the operating constraint',
      diagnosis: `Runway is ${fmtMonths(runway.months)} at current burn. Evidence work that does not preserve cash is secondary.`,
      source: 'runway',
      factIds: ['runway.status', 'runway.months'],
    };
  }
  if (commercialUninitialized) {
    return {
      id: 'constraint-state-uninitialized',
      title: 'Commercial constraint not yet assessable',
      diagnosis: 'Import the existing Dubai Top 50 before Bridge45 ranks commercial moves. Without account data, it cannot distinguish target supply, access, conversion, qualification, or asset commitment.',
      source: 'setup',
      factIds: ['accounts.length'],
    };
  }
  // Before D001 blocker or opportunity-gap outcomes: low outreach cannot diagnose conversion.
  if (readyPool >= 1 && contacted < 3) {
    return {
      id: 'constraint-outreach-insufficient',
      title: 'Constraint not yet assessable — outreach has not started at sufficient volume.',
      diagnosis: 'Outreach has not started at sufficient volume to diagnose the commercial constraint.',
      source: 'funnel',
      factIds: ['funnel.counts.contacted'],
    };
  }
  if (d001.constraintOverdue && d001.constraint?.blocker) {
    return {
      id: 'constraint-d001-overdue',
      title: d001.constraint.blocker,
      diagnosis: `The D001 constraint was due ${d001.constraint.due}. A constraint that slips is the company's actual velocity.`,
      source: 'd001',
      factIds: ['d001.constraint.due'],
    };
  }
  if (leak && leak.key !== 'outreach_insufficient') {
    return {
      id: `constraint-funnel-${leak.key || 'leak'}`,
      title: leak.stage,
      diagnosis: leak.text,
      source: 'funnel',
      factIds: [`funnel.diagnosis.${leak.key || '0'}`],
    };
  }
  if (leak?.key === 'outreach_insufficient') {
    return {
      id: 'constraint-outreach-insufficient',
      title: 'Constraint not yet assessable — outreach has not started at sufficient volume.',
      diagnosis: leak.text,
      source: 'funnel',
      factIds: ['funnel.diagnosis.outreach_insufficient'],
    };
  }
  if (funnel.counts.qualified === 0 && sprint.day >= 5 && contacted >= 3) {
    return {
      id: 'constraint-no-qualified',
      title: 'No real Dubai opportunity yet',
      diagnosis: `Day ${sprint.day} and outreach has not yet produced a real opportunity (confirmed problem plus next commercial step). Nothing downstream compensates for that.`,
      source: 'funnel',
      factIds: ['funnel.counts.qualified'],
    };
  }
  if (d001.constraint?.blocker) {
    return {
      id: 'constraint-d001-named',
      title: d001.constraint.blocker,
      diagnosis: `Named as the current #1 blocker. Evidence needed: ${d001.constraint.evidenceNeeded || 'not set'}.`,
      source: 'd001',
      factIds: ['d001.constraint.blocker'],
    };
  }
  if (time.drifting) {
    return {
      id: 'constraint-time-drift',
      title: 'Founder time is drifting off Dubai',
      diagnosis: `Dubai is below 40% of logged founder time against a 55% target.`,
      source: 'time',
      factIds: ['time.internalShare'],
    };
  }
  return {
    id: 'constraint-quiet',
    title: 'No single recorded limiter is dominating',
    diagnosis: 'Nothing in the recorded state contradicts the strategy right now. That is not the same as progress — keep recording outcomes as outreach proceeds.',
    source: 'quiet',
    factIds: [],
  };
}

function detectDrift(state, derived) {
  const { evidence, time, funnel, top3, sprint, saudi, infra } = derived;
  const out = [];
  const weekAct = (evidence.thisWeek || []).filter((e) => e.level === 'ACT');
  const weekProof = evidence.weekProof || [];
  const openException = (state.exceptions || []).find((e) => !e.decision || e.decision === 'Pending');

  if (weekAct.length >= 5 && weekProof.length === 0) {
    out.push({
      id: 'motion',
      text: `${weekAct.length} activities logged this week and zero evidence. That is motion. The week has produced nothing the market has confirmed.`,
    });
  }

  const noObjective = weekAct.filter((e) => !String(e.linkedTo || '').trim() && !String(e.description || '').trim());
  if (noObjective.length >= 3) {
    out.push({
      id: 'no-objective',
      text: `${noObjective.length} activity records this week have no evidence objective. Activity without an evidence objective is not execution.`,
    });
  }

  const c = funnel.counts || {};
  if ((c.contacted || 0) >= 3 && (c.engaged || 0) / Math.max(1, c.contacted) < 0.25) {
    out.push({
      id: 'research-volume',
      text: `${c.contacted} accounts contacted and ${c.engaged} engaged. Do not add more accounts; improve access and outreach conversion.`,
    });
  } else if ((c.engaged || 0) >= 3 && (c.discovery || 0) / Math.max(1, c.engaged) < 0.35) {
    out.push({
      id: 'conversation-volume',
      text: `${c.engaged} engagements produced ${c.discovery} commercial conversations. Improve conversation conversion before adding volume.`,
    });
  }

  const product = (state.productRequests || []).filter((r) => r.classification === 'FROZEN' || !r.opportunityId);
  if (product.length) {
    out.push({
      id: 'speculative-product',
      text: `${product.length} product request(s) are frozen or unattached to an opportunity. Speculative product work is not a gate.`,
    });
  }

  const social = (state.evidence || []).filter((e) =>
    /social|linkedin|twitter|content|posting|newsletter/i.test(`${e.title} ${e.description || ''}`),
  );
  if (social.length && !social.some((e) => weightOf(e.level) >= 3)) {
    out.push({
      id: 'social',
      text: 'Social-media or content work is recorded without a gate or E3+ objective. Stop it.',
    });
  }

  const qatar = (state.evidence || []).filter((e) => /qatar|bahrain/i.test(`${e.title} ${e.counterparty} ${e.market || ''}`));
  if (qatar.length && !openException) {
    out.push({
      id: 'qatar',
      text: 'Qatar/Bahrain work is recorded with no Strategy Exception. Those markets are parked.',
    });
  }

  if (saudi.count < 2 && time.rows?.find((r) => r.key === 'saudi')?.actual > 25) {
    out.push({
      id: 'saudi-allocation',
      text: `Saudi has ${saudi.count} of 4 signals but is taking more than a quarter of founder time. Saudi has not earned that.`,
    });
  }

  const partnerMeetings = (state.d001?.partners || []).filter((p) => /meeting|intro|coffee/i.test(`${p.nextAction || ''} ${p.notes || ''}`));
  if (partnerMeetings.length >= 3 && derived.d001.partnerReady.length === 0) {
    out.push({
      id: 'partner-meetings',
      text: 'Partner meetings are accumulating without a Pilot Ready counterparty. Low-value partnership motion.',
    });
  }

  const staleE3 = (state.opportunities || []).filter((o) => {
    const w = evidenceWeight(o.evidenceLevel);
    if (w < 3 || w > 4) return false;
    if (o.stage === 'Won / Lost / Parked') return false;
    return sprint.executionAsOf && o.nextActionDue && isAfter(sprint.executionAsOf, o.nextActionDue);
  });
  if (staleE3.length) {
    out.push({
      id: 'stale-e3',
      text: `${staleE3.length} E3/E4 opportunit${staleE3.length === 1 ? 'y is' : 'ies are'} stale against their own next action.`,
    });
  }

  if (funnel.primary && c.candidates <= 1 && sprint.day < 20) {
    out.push({
      id: 'premature-primary',
      text: 'Primary D001 is designated with no credible backup. Premature attachment raises concentration risk.',
    });
  }

  if (time.totalHours > 0 && time.internalShare > 30) {
    out.push({
      id: 'admin-time',
      text: `DRIFT DETECTED. ${Math.round((time.internalShare / 100) * time.totalHours)} founder hours this week went to work that does not advance a current gate or E3+ opportunity (${time.internalShare}% internal).`,
    });
  }

  if ((top3.open || []).length >= 12) {
    out.push({
      id: 'backlog-growth',
      text: `${top3.open.length} open moves. Excessive queue growth is how a war room becomes a project-management app.`,
    });
  }

  if (infra.customWithoutDeal?.length) {
    out.push({
      id: 'custom-product',
      text: `${infra.customWithoutDeal.length} CUSTOM product item(s) with no opportunity attached.`,
    });
  }

  return out;
}

function stopWatch(state, derived, evals) {
  const stop = [];
  const watch = [];
  const { runway, funnel, infra, top3 } = derived;
  const c = funnel.counts || {};

  if ((c.contacted || 0) >= 3 && (c.engaged || 0) / Math.max(1, c.contacted) < 0.25) {
    stop.push({
      text: 'Adding more accounts',
      why: `${c.contacted} accounts contacted and ${c.engaged} engaged. The problem is access, not volume.`,
    });
  }
  if ((c.discovery || 0) >= 6 && c.qualified <= Math.floor(c.discovery / 5)) {
    stop.push({
      text: 'More discovery volume without reviewing ICP',
      why: `${c.discovery} conversations produced ${c.qualified} real opportunit${c.qualified === 1 ? 'y' : 'ies'}. Review the ICP and value proposition.`,
    });
  }
  if (runway.stopSpend > 0) {
    stop.push({
      text: 'STOP-category spend',
      why: `${fmtMoney(runway.stopSpend, state.settings?.currency)} of sprint spend sits in STOP and is still being spent.`,
    });
  }
  if (evals.timeDrifting) {
    stop.push({
      text: 'Internal / admin founder time',
      why: `${evals.timeInternalShare}% of logged hours are internal against a 5% Other ceiling.`,
    });
  }

  if (c.qualified >= 3 && c.assetsIdentified === 0) {
    watch.push({
      text: 'Commitment conversion',
      why: `${c.qualified} qualified sponsors have not shared assets. The current problem is commitment conversion.`,
    });
  }
  if (funnel.primary && c.backups === 0) {
    watch.push({
      text: 'Deal concentration',
      why: 'Primary D001 is progressing but there is no credible backup. Deal concentration is high.',
    });
  }
  if (infra.closest && !infra.closest.nextAction) {
    watch.push({
      text: `Infrastructure — ${infra.closest.account || 'unnamed'}`,
      why: `Closest path to cash is at ${infra.closest.stage} with no next step. That is the problem.`,
    });
  }
  if ((top3.overdue || []).length) {
    watch.push({
      text: `${top3.overdue.length} overdue Critical/High action(s)`,
      why: 'Overdue high-priority work is the actual calendar.',
    });
  }
  if (derived.runway.status === 'watch') {
    watch.push({
      text: 'Runway entering the watch band',
      why: `${fmtMonths(derived.runway.months)} at current burn.`,
    });
  }
  return { stop: stop.slice(0, 4), watch: watch.slice(0, 4) };
}

function initializationStatus(state, derived) {
  const integrityOk = derived.sprint?.integrity?.ok !== false;
  const accounts = state.accounts || [];
  const operating = deriveOperatingMode(state, derived);
  const runwayComplete = operating.runwayOk;
  const queueDone = operating.queueOk;
  const items = [
    { id: 'clock', label: 'Confirm sprint clock', done: integrityOk && !!derived.sprint?.start, minutes: 2 },
    { id: 'runway', label: 'Runway baseline or Unknown/proceed', done: runwayComplete, minutes: 5 },
    { id: 'time', label: 'Establish time capture (optional)', done: !!derived.time.hasMinimumHours, minutes: 5 },
    { id: 'import', label: 'Import Dubai accounts', done: accounts.length > 0, minutes: 8 },
    { id: 'verify', label: 'Batch-confirm founder queue', done: queueDone, minutes: 10 },
  ];
  const complete = items.filter((i) => i.done).length;
  const remainingMinutes = items.filter((i) => !i.done && i.id !== 'time').reduce((s, i) => s + i.minutes, 0);
  return {
    items,
    complete,
    total: items.length,
    remainingMinutes,
    ready: operating.mode === 'ready',
    /** Provisional AI allowed once any accounts exist — setup is not a hard barrier. */
    commercialReady: accounts.length > 0,
    operatingMode: operating.mode,
    operating,
  };
}

function shouldAbstain(state, derived) {
  const missing = [];
  const accounts = (state.accounts || []).length;
  const runwayUnknownProceed = !!state.ui?.runwayUnknownProceed;
  if ((derived.runway.cashUnknown || derived.runway.burnUnknown) && !runwayUnknownProceed) {
    const runwayGaps = (derived.runway.missingForCurrent || []).join(', ') || 'Cash on hand, current monthly burn';
    missing.push(`Runway baseline not recorded — missing: ${runwayGaps}.`);
  }
  if (!derived.time.hasMinimumHours) {
    missing.push('Founder-time baseline is not recorded.');
  }
  if (accounts === 0) {
    missing.push('Dubai funnel is uninitialized — no accounts imported.');
  }
  const open = (derived.top3.open || []).filter(isOpenAction);
  const eligible = recommendReadyMoves(open, {
    sprint: derived.sprint,
    funnel: derived.funnel,
    time: derived.time,
    exceptions: state.exceptions,
    state,
    constraintTitle: derived.d001?.constraint?.blocker,
  });
  if (eligible.length === 0 && accounts === 0) {
    missing.push('No eligible commercial moves until accounts are in scope.');
  }
  return {
    abstain: missing.length > 0 && accounts === 0,
    missing,
    setup: accounts < 10 || ((derived.runway.cashUnknown || derived.runway.burnUnknown) && !runwayUnknownProceed),
  };
}

function isSetupMove(action, state) {
  if (action?.expectedEvidence === 'OPS' || action?.evidenceCredit === 'none') return true;
  const pkg = (state.workPackages || []).find((p) => p.id === action?.workPackageId);
  return !!pkg?.setup;
}

function recommendTop3(state, derived, { lastCloseout, overrides }) {
  const init = initializationStatus(state, derived);
  if (!init.commercialReady) return [];

  const open = (derived.top3.open || []).filter(isOpenAction);
  const ctx = {
    sprint: derived.sprint,
    d001: derived.d001,
    funnel: derived.funnel,
    time: derived.time,
    exceptions: state.exceptions,
    state,
    constraintTitle: derived.d001?.constraint?.blocker,
  };
  let picked = recommendReadyMoves(open, ctx, PRIORITY_CONFIG_V1)
    .filter((a) => !isSetupMove(a, state));

  if (lastCloseout?.q3) {
    const roll = String(lastCloseout.q3).toLowerCase();
    const matches = open.filter((a) => roll && roll.includes(String(a.text || '').slice(0, 24).toLowerCase()));
    if (matches.length) {
      const rest = picked.filter((p) => !matches.some((m) => m.id === p.id));
      picked = [...matches, ...rest].slice(0, 3);
    }
  }

  return picked
    .filter((a) => !suppressed(overrides, 'promote_top3', a.id))
    .slice(0, 3)
    .map((a, i) => ({
      id: recId('promote_top3', a.id),
      kind: 'promote_top3',
      status: 'pending',
      why: whyAction(a, derived),
      scoringConfigVersion: a.scoringConfigVersion || scoringConfigVersion(),
      payload: {
        actionId: a.id,
        actionCandidateId: a.id,
        rank: i + 1,
        text: a.text,
        todayRank: i + 1,
        linkedKind: a.linkedKind || '',
        linkedId: a.linkedId || '',
        linkedLabel: a.linkedLabel || '',
        expectedEvidence: a.expectedEvidence || 'E3',
        gateRelevance: a.gateRelevance || 'none',
        delayConsequence: delayConsequence(a, derived),
        whyNow: whyAction(a, derived),
        subject: a.id,
        workPackageId: a.workPackageId || '',
        source: a.source || 'Rule',
        scoringConfigVersion: a.scoringConfigVersion || scoringConfigVersion(),
      },
    }));
}

function extraRecs(state, derived, evals, overrides) {
  const recs = [];
  const today = derived.sprint.today;

  for (const a of derived.top3.overdue || []) {
    if (suppressed(overrides, 'roll_forward', a.id)) continue;
    recs.push({
      id: recId('roll_forward', a.id),
      kind: 'roll_forward',
      status: 'pending',
      why: `Because "${a.text}" is overdue and still open. Rolling the date is honest; cloning a new task is not.`,
      payload: {
        actionId: a.id,
        text: a.text,
        dueDate: today,
        subject: a.id,
        whyNow: 'It is overdue.',
        expectedEvidence: a.expectedEvidence,
        gateRelevance: a.gateRelevance,
        linkedLabel: a.linkedLabel,
        delayConsequence: delayConsequence(a, derived),
      },
    });
  }

  for (const o of derived.top3.missingOppActions || []) {
    if (suppressed(overrides, 'create_next_action', o.id)) continue;
    recs.push({
      id: recId('create_next_action', o.id),
      kind: 'create_next_action',
      status: 'pending',
      why: `Because ${o.name || o.accountName || 'this opportunity'} is E3+ with no dated next founder action.`,
      payload: {
        opportunityId: o.id,
        text: `Next commercial step: ${o.name || o.accountName || 'opportunity'} — get the evidence that unlocks the next stage.`,
        expectedEvidence: weightOf(o.evidenceLevel) >= 4 ? 'E5' : 'E4',
        workstream: 'Dubai',
        linkedKind: 'opportunity',
        linkedId: o.id,
        linkedLabel: o.name || o.accountName || '',
        gateRelevance: String(derived.sprint.nextGate?.day || 'none'),
        subject: o.id,
        whyNow: 'An E3/E4 opportunity without a next action is drift.',
        delayConsequence: 'The conversation goes stale and the gate is judged on activity.',
      },
    });
  }

  for (const a of derived.top3.blockedUnnamed || []) {
    if (suppressed(overrides, 'escalate_blocker', a.id)) continue;
    recs.push({
      id: recId('escalate_blocker', a.id),
      kind: 'escalate_blocker',
      status: 'pending',
      why: `Because "${a.text}" is blocked with no named counterparty, permission, or missing input.`,
      payload: {
        actionId: a.id,
        text: a.text,
        subject: a.id,
        blocker: 'Name the counterparty, permission, or missing input.',
        whyNow: 'An unnamed blocker cannot be worked.',
        expectedEvidence: a.expectedEvidence,
        gateRelevance: a.gateRelevance,
        delayConsequence: 'The action sits in Blocked forever.',
      },
    });
  }

  if (evals.betterCandidate && !suppressed(overrides, 'change_primary_d001', evals.betterCandidate.id)) {
    recs.push({
      id: recId('change_primary_d001', evals.betterCandidate.id),
      kind: 'change_primary_d001',
      status: 'pending',
      why: `Because ${evals.betterCandidate.name} scores ${evals.betterCandidate.score}/50 against ${evals.betterCandidate.primaryScore}/50 for the current Primary. Do not keep the incumbent because it arrived first.`,
      payload: {
        candidateId: evals.betterCandidate.id,
        candidateName: evals.betterCandidate.name,
        subject: evals.betterCandidate.id,
        score: evals.betterCandidate.score,
        primaryScore: evals.betterCandidate.primaryScore,
        whyNow: 'Evidence now favours another candidate.',
        expectedEvidence: 'E3',
        gateRelevance: String(derived.sprint.nextGate?.day || 'none'),
        delayConsequence: 'The sprint concentrates on the weaker deal.',
        confirmation:
          'Primary opportunity changed based on evidence. The underlying strategy remains locked.',
      },
    });
  }

  if (evals.saudiEarned && derived.sprint.day < 30 && !suppressed(overrides, 'propose_strategy_exception', 'saudi')) {
    const openException = (state.exceptions || []).find((e) => !e.decision || e.decision === 'Pending');
    if (!openException) {
      recs.push({
        id: recId('propose_strategy_exception', 'saudi'),
        kind: 'propose_strategy_exception',
        status: 'pending',
        why: `Because Saudi is carrying ${evals.saudiCount} strong signals. That pattern would justify a Strategy Exception — the form, not enthusiasm. The allocation stays locked until decided.`,
        payload: {
          trigger: 'Material inbound pull',
          subject: 'saudi',
          whyNow: 'Saudi has earned a hearing, not an automatic reallocation.',
          expectedEvidence: 'E4',
          gateRelevance: '30',
          delayConsequence: 'Either Saudi work continues off-strategy, or evidence is ignored.',
        },
      });
    }
  }

  const lowValue = derived.top3.lowValue || [];
  if (lowValue.length >= 3) {
    const a = lowValue[0];
    if (a && !suppressed(overrides, 'cancel', a.id)) {
      recs.push({
        id: recId('cancel', a.id),
        kind: 'cancel',
        status: 'pending',
        why: 'Because completed actions like this produced no commercial evidence. Cancel the pattern, not just the row.',
        payload: {
          actionId: a.id,
          text: a.text,
          subject: a.id,
          whyNow: 'Low-value completed work is repeating.',
          expectedEvidence: a.expectedEvidence,
          gateRelevance: a.gateRelevance,
          delayConsequence: 'The queue keeps rewarding motion.',
        },
      });
    }
  }

  return recs;
}

function fridayDigest(state, derived, constraint) {
  const scc = stopContinueConcentrate(state, derived);
  const cont = scc.filter((x) => /^CONTINUE/i.test(x)).join(' ') || 'The motions that produced E3+ evidence. Repeat those exact conversations.';
  const stop = scc.filter((x) => /^STOP/i.test(x)).join(' ') || 'Work that consumed hours without proof.';
  const conc = scc.filter((x) => /^CONCENTRATE/i.test(x)).join(' ') || 'Dubai D001. It is the only market expected to produce a transaction this sprint.';
  const missing = deriveGateReadiness(state, derived, derived.sprint.nextGate?.day).smallestActions[0];
  return {
    continue: cont.replace(/^CONTINUE:\s*/i, ''),
    stop: stop.replace(/^STOP:\s*/i, ''),
    concentrate: conc.replace(/^CONCENTRATE:\s*/i, ''),
    constraint: constraint.title,
    nextObjective: missing || constraint.title,
  };
}

export function composeBriefing(state, derived, extras = {}) {
  const { lastCloseout = null, overrides = [], includeFriday = false } = extras;
  const evaluations = evaluateMorning(state, derived);
  if (lastCloseout?.q4 && String(lastCloseout.q4).trim() && (state.accounts || []).length) {
    evaluations.constraint = {
      id: 'constraint-closeout',
      title: String(lastCloseout.q4).trim(),
      diagnosis: 'Named at yesterday’s closeout as a newly revealed constraint.',
      source: 'closeout',
      factIds: ['closeout.q4'],
    };
  }
  const constraint = evaluations.constraint;
  const gateRisk = deriveGateReadiness(state, derived, derived.sprint.nextGate?.day);
  const top3 = recommendTop3(state, derived, { lastCloseout, overrides });
  const extrasRecs = extraRecs(state, derived, evaluations, overrides);
  const drift = detectDrift(state, derived);
  const { stop, watch } = stopWatch(state, derived, evaluations);
  const friday = (includeFriday || isoWeekday(derived.sprint.today) === 5) && derived.sprint.phase !== 'pre'
    ? fridayDigest(state, derived, constraint)
    : null;

  const abstain = shouldAbstain(state, derived);
  const initialization = initializationStatus(state, derived);
  // Provisional recommendations once accounts exist — setup mode does not block AI guidance.
  const recommendations = initialization.commercialReady ? [...top3, ...extrasRecs] : [];

  return {
    date: derived.sprint.today,
    evaluations,
    constraint,
    constraintId: constraint.id,
    diagnosis: constraint.diagnosis,
    top3,
    gateRisk,
    gateSummary: gateRisk.compact || null,
    stop,
    watch,
    drift,
    funnel: (derived.funnel.diagnosis || []).map((d) => ({ key: d.key, stage: d.stage, text: d.text })),
    d001Change: evaluations.betterCandidate || null,
    strategyException: recommendations.find((r) => r.kind === 'propose_strategy_exception') || null,
    friday,
    recommendations,
    abstain: abstain.abstain,
    missingData: abstain.missing,
    initialization,
    operatingMode: initialization.operatingMode,
    scoringConfigVersion: scoringConfigVersion(),
    fingerprint: briefingFingerprint(state),
  };
}

export function briefingFingerprint(state) {
  return JSON.stringify({
    e: (state.evidence || []).map((x) => x.id),
    a: (state.top3 || []).map((x) => [x.id, x.status, x.selectedForToday, x.dueDate, x.disposition]),
    c: (state.closeouts || []).map((x) => x.id),
    o: (state.opportunities || []).map((x) => [x.id, x.stage, x.evidenceLevel, x.waitingExternal]),
    acc: (state.accounts || []).map((x) => x.id),
    r: [state.runway?.cashToday, state.runway?.monthlyBurn],
    t: (state.timeLog || []).map((x) => x.weekStart),
    d: (state.recommendationDecisions || []).length,
    s: state.settings?.sprint?.start || state.settings?.sprintStart,
  });
}

export function compactAskContext(briefing) {
  if (!briefing) return {};
  return {
    date: briefing.date,
    constraint: briefing.constraint,
    diagnosis: briefing.diagnosis,
    missingData: briefing.missingData || [],
    top3: (briefing.top3 || []).map((r, i) => ({
      why: r.why,
      payload: {
        actionCandidateId: r.payload?.actionCandidateId || r.payload?.actionId || r.id,
        actionId: r.payload?.actionId,
        rank: r.payload?.rank || i + 1,
        text: r.payload?.text,
        expectedEvidence: r.payload?.expectedEvidence,
        gateRelevance: r.payload?.gateRelevance,
      },
    })),
    gateRisk: {
      day: briefing.gateRisk?.day,
      missing: (briefing.gateRisk?.missing || []).map((m) => m.label),
      atRisk: (briefing.gateRisk?.atRisk || []).map((m) => m.label),
      likelyPass: (briefing.gateRisk?.likelyPass || []).map((m) => m.label),
    },
    stop: briefing.stop,
    watch: briefing.watch,
    drift: briefing.drift,
    funnel: briefing.funnel,
    d001Change: briefing.d001Change,
    friday: briefing.friday,
  };
}

function asAskItems(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((raw) => {
      if (typeof raw === 'string') return { claim: raw.trim(), recordRefs: [] };
      if (!raw || typeof raw !== 'object') return { claim: String(raw), recordRefs: [] };
      const claim = String(raw.claim || raw.text || raw.rationale || raw.whyNow || raw.title || '').trim();
      const refs = Array.isArray(raw.recordRefs) ? raw.recordRefs.map(String) : [];
      if (raw.actionCandidateId) refs.push(String(raw.actionCandidateId));
      if (raw.linkedId) refs.push(String(raw.linkedId));
      return { claim, recordRefs: refs.filter(Boolean) };
    })
    .filter((item) => item.claim);
}

/** Map model JSON (Ask contract, observations, or confused planner shape) into UI buckets. */
export function mapAskStructured(parsed, packet) {
  const empty = { facts: [], inferences: [], recommendations: [], missing: [], text: '' };
  if (!parsed || typeof parsed !== 'object') return empty;
  const observations = Array.isArray(parsed.observations) ? parsed.observations : [];
  const obsFacts = observations.filter((o) => String(o.kind || '') === 'fact');
  const obsInf = observations.filter((o) => String(o.kind || '') === 'inference');
  const facts = asAskItems(parsed.facts).length
    ? asAskItems(parsed.facts)
    : asAskItems(obsFacts.length ? obsFacts : parsed.constraintExplanation ? [parsed.constraintExplanation] : []);
  const inferences = asAskItems(parsed.inferences).length ? asAskItems(parsed.inferences) : asAskItems(obsInf);
  let recommendations = asAskItems(parsed.recommendations);
  if (!recommendations.length) recommendations = asAskItems(parsed.rankedActionExplanations);
  if (!recommendations.length) recommendations = asAskItems(parsed.suggestedTop3);
  const missing = asAskItems(parsed.missing ?? parsed.missingInformation ?? packet?.missingData ?? []);
  let text = String(parsed.briefing || parsed.answer || '').trim();
  if (!text) {
    text = [...facts, ...inferences, ...recommendations].map((i) => i.claim).filter(Boolean).join('\n\n');
  }
  return { facts, inferences, recommendations, missing, text };
}

export function structureAskAnswer(question, briefing) {
  const q = String(question || '');
  const empty = {
    facts: [],
    inferences: [],
    recommendations: [],
    missing: briefing?.missingData || [],
    text: '',
  };
  if (!briefing) {
    return { ...empty, text: 'No briefing has been composed from recorded state yet.', missing: ['No briefing'] };
  }
  const constraintFact = briefing.constraint
    ? { claim: briefing.constraint.title, recordRefs: briefing.constraint.factIds || [briefing.constraint.id || 'constraint'], kind: 'fact' }
    : null;
  const first = briefing.top3?.[0];

  if (/#1|priority|why is this/i.test(q)) {
    const facts = [constraintFact].filter(Boolean);
    const recs = first
      ? [{ claim: first.payload?.text, recordRefs: [first.payload?.actionCandidateId || first.payload?.actionId].filter(Boolean), kind: 'recommendation' }]
      : [];
    const inferences = first ? [{ claim: first.why, recordRefs: first.payload?.actionId ? [first.payload.actionId] : [], kind: 'inference' }] : [];
    const missing = briefing.missingData || [];
    const text = first
      ? `${first.payload?.text}\n\nWhy? ${first.why}`
      : `Current constraint: ${briefing.constraint?.title}. ${briefing.diagnosis}`;
    return { facts, inferences, recommendations: recs, missing, text };
  }
  if (/d001|candidate|strongest/i.test(q)) {
    if (briefing.d001Change) {
      const text = `${briefing.d001Change.name} scores ${briefing.d001Change.score}/50 against ${briefing.d001Change.primaryScore}/50 for the current Primary. Changing Primary requires founder confirmation. The underlying strategy remains locked.`;
      return {
        facts: [{ claim: `${briefing.d001Change.name} score ${briefing.d001Change.score}/50`, recordRefs: [briefing.d001Change.id], kind: 'fact' }],
        inferences: [{ claim: 'Evidence now favours another candidate.', recordRefs: [briefing.d001Change.id], kind: 'inference' }],
        recommendations: [{ claim: 'Confirm a Primary change; the governor cannot apply it alone.', recordRefs: [briefing.d001Change.id], kind: 'recommendation' }],
        missing: [],
        text,
      };
    }
    const text = briefing.evaluations?.primaryStillBest
      ? 'The recorded scores do not currently justify replacing Primary D001.'
      : 'No Primary is designated yet. Comparison without a decision is drift.';
    return { facts: [constraintFact].filter(Boolean), inferences: [{ claim: text, recordRefs: [], kind: 'inference' }], recommendations: [], missing: briefing.primary ? [] : ['No Primary D001 designated'], text };
  }
  if (/gate|day 15|day 30|day 45|pass/i.test(q)) {
    const g = briefing.gateRisk;
    const missing = (g?.missing || []).map((m) => m.label);
    const text = `${g?.label || 'Next gate'}. Missing: ${missing.join('; ') || 'nothing material'}. Smallest actions: ${(g?.smallestActions || []).join(' ')}`;
    return {
      facts: (g?.likelyPass || []).map((m) => ({ claim: m.label, recordRefs: ['gateRisk'], kind: 'fact' })),
      inferences: [{ claim: text, recordRefs: ['gateRisk'], kind: 'inference' }],
      recommendations: (g?.smallestActions || []).map((a) => ({ claim: a, recordRefs: ['gateRisk'], kind: 'recommendation' })),
      missing,
      text,
    };
  }
  if (/saudi/i.test(q)) {
    const text = briefing.evaluations?.saudiEarned
      ? `Saudi has ${briefing.evaluations.saudiCount} strong signals. That earns a Strategy Exception hearing, not an automatic reallocation.`
      : `Saudi has ${briefing.evaluations?.saudiCount ?? 0} of 4 signals. It has not earned more attention.`;
    return {
      facts: [{ claim: `${briefing.evaluations?.saudiCount ?? 0} / 4 Saudi signals`, recordRefs: ['saudi.signals'], kind: 'fact' }],
      inferences: [{ claim: text, recordRefs: ['saudi.signals'], kind: 'inference' }],
      recommendations: [],
      missing: [],
      text,
    };
  }
  if (/stop/i.test(q)) {
    const stops = briefing.stop || [];
    const text = stops.map((s) => `${s.text} — ${s.why}`).join('\n') || 'Nothing in the recorded state is clearly stop-worthy today.';
    return {
      facts: stops.map((s) => ({ claim: s.text, recordRefs: [], kind: 'fact' })),
      inferences: stops.map((s) => ({ claim: s.why, recordRefs: [], kind: 'inference' })),
      recommendations: stops.map((s) => ({ claim: `Stop: ${s.text}`, recordRefs: [], kind: 'recommendation' })),
      missing: [],
      text,
    };
  }
  if (/dies|backup|next best|primary disappears/i.test(q)) {
    const conc = (briefing.watch || []).find((w) => /concentration/i.test(w.text));
    const text = conc ? conc.why : 'If Primary dies, the next path is the highest-scoring backup candidate. If none exists, that absence is the constraint.';
    return {
      facts: conc ? [{ claim: conc.text, recordRefs: ['funnel.concentration'], kind: 'fact' }] : [],
      inferences: [{ claim: text, recordRefs: ['funnel.concentration'], kind: 'inference' }],
      recommendations: [],
      missing: conc ? [] : ['No backup candidate recorded'],
      text,
    };
  }
  const text = `${briefing.constraint?.title}. ${briefing.diagnosis}`;
  return {
    facts: [constraintFact].filter(Boolean),
    inferences: [{ claim: briefing.diagnosis, recordRefs: briefing.constraint?.factIds || [], kind: 'inference' }],
    recommendations: (briefing.top3 || []).slice(0, 3).map((r) => ({
      claim: r.payload?.text,
      recordRefs: [r.payload?.actionCandidateId || r.payload?.actionId].filter(Boolean),
      kind: 'recommendation',
    })),
    missing: briefing.missingData || [],
    text,
  };
}

export function answerFromBriefing(question, briefing) {
  return structureAskAnswer(question, briefing).text;
}

export function recommendationDecision({ rec, status, reason, replacementMoveId, snapshotId, scoringConfigVersion: ver, today }) {
  return {
    kind: rec?.kind,
    recommendationId: rec?.id,
    status,
    reason: reason || '',
    replacementMoveId: replacementMoveId || '',
    snapshotId: snapshotId || '',
    scoringConfigVersion: ver || rec?.scoringConfigVersion || scoringConfigVersion(),
    date: today,
    actionId: rec?.payload?.actionId || rec?.payload?.subject || '',
  };
}

/** Pure mutations for Accept. Never auto-applies D001 or strategy changes. */
export function applyRecommendation(rec, { today } = {}) {
  const p = rec.payload || {};
  switch (rec.kind) {
    case 'promote_top3':
      return {
        mutations: [
          {
            type: 'patch',
            path: 'top3',
            id: p.actionId,
            changes: {
              // AI Plan lane only — never silently pin founder selectedForToday
              aiPlanRank: p.todayRank || p.rank || 1,
              status: p.status || 'Ready',
              source: p.source || 'BridgeAI',
              whyNow: p.whyNow || rec.why || '',
              autoSelected: true,
            },
          },
        ],
      };
    case 'reprioritize':
      return {
        mutations: [{ type: 'patch', path: 'top3', id: p.actionId, changes: { priority: p.priority } }],
      };
    case 'roll_forward':
      return {
        mutations: [
          {
            type: 'patch',
            path: 'top3',
            id: p.actionId,
            changes: { dueDate: p.dueDate || today || addDays(today, 1) },
          },
        ],
      };
    case 'park':
      return {
        mutations: [
          { type: 'patch', path: 'top3', id: p.actionId, changes: { status: 'Closed', disposition: 'stopped', closedAt: today, selectedForToday: false } },
        ],
      };
    case 'cancel':
      return {
        mutations: [
          { type: 'patch', path: 'top3', id: p.actionId, changes: { status: 'Closed', disposition: 'stopped', closedAt: today, selectedForToday: false } },
        ],
      };
    case 'create_next_action':
      return {
        mutations: [
          {
            type: 'add',
            path: 'top3',
            item: newAction(
              {
                text: p.text,
                workstream: p.workstream || 'Dubai',
                expectedEvidence: p.expectedEvidence || 'E3',
                gateRelevance: p.gateRelevance || 'none',
                linkedKind: p.linkedKind || 'opportunity',
                linkedId: p.linkedId,
                linkedLabel: p.linkedLabel,
                priority: 'High',
                status: 'Ready',
                source: 'Rule',
              },
              today,
            ),
          },
        ],
      };
    case 'escalate_blocker':
      return {
        mutations: [
          {
            type: 'patch',
            path: 'top3',
            id: p.actionId,
            changes: { status: 'Blocked', blocker: p.blocker || 'Named blocker required' },
          },
        ],
      };
    case 'change_primary_d001':
      return { modal: 'designate', payload: p, mutations: [] };
    case 'propose_strategy_exception':
      return { modal: 'exception', payload: p, mutations: [] };
    default:
      return { mutations: [] };
  }
}

/** Accept a recommendation and, when linked, emit a CRM write-back payload. */
export function applyAccept(rec, { today, action } = {}) {
  const result = applyRecommendation(rec, { today });
  const source = action || rec?.payload || {};
  const linkedKind = source.linkedKind;
  const linkedId = source.linkedId;
  if (!['account', 'opportunity'].includes(linkedKind) || !linkedId) {
    return { ...result, writeback: null };
  }
  return {
    ...result,
    writeback: {
      type: 'accept',
      linkedKind,
      linkedId,
      text: source.text || rec?.title || '',
      dueDate: source.dueDate || today || '',
    },
  };
}

export function unpinOthersMutations(openActions, keepIds) {
  const keep = new Set(keepIds);
  return (openActions || [])
    .filter((a) => a.selectedForToday && !keep.has(a.id))
    .map((a) => ({
      type: 'patch',
      path: 'top3',
      id: a.id,
      changes: {
        selectedForToday: false,
        todayRank: 0,
        status: a.status === 'Today' ? 'Ready' : a.status,
      },
    }));
}
